import InvalidPermissions from "../company-pages/InvalidPermissions";
import LoadingContent from "@/common/LoadingContent";
import { Actions as COMPANY_ACTIONS } from "@/redux/actions/companyActions";
import { Actions as EMPLOYEEACTIONS } from "@/redux/actions/employeeActions";
import { Actions as PAYMENT_ACTIONS } from "@/redux/actions/paymentActions";
import { CalendarIcon } from "@/icons/outline";
import { addMonths, format } from "date-fns";
import _ from "lodash";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import AddTransactionModal from "../company-settings/billing-tab/AddTransactionModal";
import IssueInvoiceModal from "../company-settings/billing-tab/IssueInvoiceModal";
import TransferCreditModal from "../company-settings/billing-tab/TransferCreditModal";
import TransferCreditToAdvisorModal from "../company-settings/billing-tab/TransferCreditToAdvisorModal";

const tabs = [
	{
		link: "/organisation/billing",
		name: "Information",
	},
	{
		link: "/organisation/billing/credit-history",
		name: "Credit History",
	},
	{
		link: "/organisation/billing/emails",
		name: "Billing Emails",
	},
];

const Billing = ({
	getCredits,
	creditBalance,
	activeEmployees,
	payrollsRun,
	creditsSpent,
	userPermissions,
	isAdmin,
	org,
	orgId,
	employeesGetListWithSalary,
	getPaymentStats,
}) => {
	const location = useLocation();
	const currentTab = location.pathname;

	const [fromDate, setFromDate] = useState(new Date(format(addMonths(new Date(), -2), "yyyy-MM-01")));
	const [toDate, setToDate] = useState(new Date(format(new Date(), "yyyy-12-31")));

	useEffect(() => {
		getPaymentStats();
		getCredits({});
	}, []);

	useEffect(() => {
		employeesGetListWithSalary({ from: format(fromDate, "yyyy-MM-dd"), to: format(toDate, "yyyy-MM-dd") });
	}, [orgId]);

	if (userPermissions === "None") return <InvalidPermissions />;

	if (!org) return <LoadingContent />;

	return (
		<>
			{isAdmin && (
				<>
					<TransferCreditModal />
					<TransferCreditToAdvisorModal />
					<IssueInvoiceModal />
					<AddTransactionModal />
				</>
			)}
			<div className="content bg-gray-lighter">
				<div className="row">
					<div className="col-sm-12">
						<div className="pull-left">
							<h1 className="page-heading">Billing</h1>
						</div>
					</div>
				</div>
				<div
					className="row items-push text-uppercase"
					style={{ paddingTop: 0, paddingBottom: 0 }}
				>
					<div className="col-xs-6 col-sm-3">
						<div
							className="font-w700 text-gray-darker"
							id="total"
						>
							Credit Balance
						</div>
						<div className="text-muted animated fadeIn">
							<small>
								<CalendarIcon style={{ height: "1em" }} /> Now
							</small>
						</div>
						<Link
							to="/organisation/billing"
							className="h2 font-w300 text-primary animated"
							href="#"
						>
							{creditBalance || 0}
						</Link>
					</div>
					<div className="col-xs-6 col-sm-3">
						<div
							className="font-w700 text-gray-darker"
							id="active"
						>
							Payrolls Run
						</div>
						<div className="text-muted animated fadeIn">
							<small>
								<CalendarIcon style={{ height: "1em" }} /> To Date
							</small>
						</div>
						<Link
							to="/organisation/payroll-history"
							className="h2 font-w300 text-primary animated"
							href="#"
						>
							{payrollsRun || 0}
						</Link>
					</div>
					<div className="col-xs-6 col-sm-3">
						<div
							className="font-w700 text-gray-darker"
							id="gross-pay"
						>
							Credits Spent
						</div>
						<div className="text-muted animated fadeIn">
							<small>
								<CalendarIcon style={{ height: "1em" }} /> To Date
							</small>
						</div>
						<Link
							to="/organisation/billing"
							className="h2 font-w300 text-primary animated"
						>
							{creditsSpent || 0}
						</Link>
					</div>
					<div className="col-xs-6 col-sm-3">
						<div
							className="font-w700 text-gray-darker"
							id="tax-paid"
						>
							Active Employees
						</div>
						<div className="text-muted animated fadeIn">
							<small>
								<CalendarIcon style={{ height: "1em" }} /> Now
							</small>
						</div>
						<Link
							to="/organisation/people"
							className="h2 font-w300 text-primary animated"
						>
							{activeEmployees || 0}
						</Link>
					</div>
				</div>
			</div>
			<ul className="nav nav-tabs nav-tabs-alt nav-justified">
				{tabs.map((tab) => {
					return (
						<li className={currentTab === tab.link ? "active" : ""}>
							<Link to={tab.link}>{tab.name}</Link>
						</li>
					);
				})}
			</ul>
		</>
	);
};

const mapStateToProps = (state) => {
	const { creditBalance, activeEmployees, creditsSpent, advisorDetails } = state.payment?.stats || {};

	const businessDetails = state?.company?.settings;

	const userPermissions = state?.permissions?.my?.permissions?.billing;
	const bundles =
		state.payment.bundles &&
		_.orderBy(
			state.payment.bundles.filter((b) => b.isPlan),
			["credits"],
			["asc"],
		);

	const currency = state.company?.settings?.currency;
	const planOptions = bundles?.map((item) => ({
		...item,
		value: item.id,
		label: `${item.name} - ${currency}${item.price}/employee/mo`,
	}));
	const selectedPlan = planOptions?.find((item) => item.value == state.company?.settings?.plan);

	const usesAdvisor =
		businessDetails?.advisor && businessDetails.paidByAdvisor && businessDetails?.advisor === advisorDetails?.id;

	return {
		bills: state.payment.credits || [],
		hasSelectedCredits: state.payment.hasSelectedCredits,
		payPeriodDetails: state.payPeriods?.periods,
		creditBalance,
		activeEmployees,
		payrollsRun: state.payPeriods?.periods?.length,
		creditsSpent,
		userPermissions,
		isAdmin: state?.my?.profile?.isAdmin,
		org: state?.company?.settings.org,
		businessDetails,
		advisorDetails: usesAdvisor ? advisorDetails : {},
		currency,
		refCode: String(
			usesAdvisor ? advisorDetails?.billingReference : businessDetails?.billingReference,
		).toUpperCase(),
		orgId: state?.company?.settings?.org,
		employeeListLength: state?.employees?.employeeListWithSalary?.length,
		selectedPlan,
		advisorPlan: usesAdvisor ? advisorDetails?.plan : undefined,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getCredits: ({ before, after }) => dispatch(PAYMENT_ACTIONS.getCredits({ before, after })),
	getPaymentStats: () => dispatch(PAYMENT_ACTIONS.getPaymentStats()),
	showPricingModal: () => dispatch(PAYMENT_ACTIONS.showPricingModal(true)),
	setBillingModal: (billingModal) => dispatch(COMPANY_ACTIONS.setBillingModal(billingModal)),
	employeesGetListWithSalary: ({ from, to }) =>
		dispatch(
			EMPLOYEEACTIONS.employeesGetListWithSalary(
				{
					from: from ? from : format(addMonths(new Date(), -2), "yyyy-MM-01"), // get list from last 2 months
					to: to ? to : format(new Date(), "yyyy-12-31"),
				},
				{ includeTax: true },
			),
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(Billing);
